import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowLeft, LogOut, Save, Send, Tag, Mail, Bell, FileText, Eye, EyeOff, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_media-insights-26/artifacts/jyhdvnlr_logo-png-cuadrado.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSettings() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [metaTesting, setMetaTesting] = useState(false);
  const [form, setForm] = useState({
    resend_api_key: "",
    sender_email: "",
    notify_email: "",
    gtm_id: "",
    voucher_emails_enabled: true,
    team_notifications_enabled: true,
    meta_pixel_id: "",
    meta_capi_token: "",
    meta_test_event_code: "",
    meta_capi_enabled: true,
  });
  const [meta, setMeta] = useState({
    resend_api_key_masked: "", resend_api_key_set: false,
    meta_capi_token_masked: "", meta_capi_token_set: false,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/settings`);
      setMeta({
        resend_api_key_masked: res.data.resend_api_key_masked,
        resend_api_key_set: res.data.resend_api_key_set,
        meta_capi_token_masked: res.data.meta_capi_token_masked || "",
        meta_capi_token_set: res.data.meta_capi_token_set || false,
      });
      setForm({
        resend_api_key: "",
        sender_email: res.data.sender_email || "",
        notify_email: res.data.notify_email || "",
        gtm_id: res.data.gtm_id || "",
        voucher_emails_enabled: res.data.voucher_emails_enabled,
        team_notifications_enabled: res.data.team_notifications_enabled,
        meta_pixel_id: res.data.meta_pixel_id || "",
        meta_capi_token: "",
        meta_test_event_code: res.data.meta_test_event_code || "",
        meta_capi_enabled: res.data.meta_capi_enabled !== false,
      });
    } catch {
      toast.error("No se pudo cargar la configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, form);
      toast.success("Configuración guardada");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const sendMetaTest = async () => {
    setMetaTesting(true);
    try {
      await axios.post(`${API}/admin/settings/meta-test`, {});
      toast.success("Evento Lead de prueba enviado a Meta.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar evento a Meta");
    } finally {
      setMetaTesting(false);
    }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) {
      toast.error("Introduce un email de prueba");
      return;
    }
    setTesting(true);
    try {
      const res = await axios.post(`${API}/admin/settings/test-email`, { email: testEmail.trim() });
      toast.success(`Email de prueba enviado (id: ${res.data.email_id?.slice(0, 8)}…)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar email de prueba");
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8FA]">
      <header className="bg-white border-b border-[#E3DDE8] sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO} alt="Core" className="h-9 w-9 object-contain"/>
            <div>
              <div className="font-display font-bold text-[#2E2638] leading-none">CORE COLCHÓN</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#8B1538] font-bold">Configuración</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-crm" className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
              <ArrowLeft size={14}/> Volver al CRM
            </Link>
            <span className="text-xs text-[#5C5366] hidden sm:block">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 text-[#5C5366] hover:text-[#8B1538] transition-colors">
              <LogOut size={14}/> Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-10 py-10">
        {loading ? (
          <div className="text-center text-[#5C5366] py-16">Cargando configuración…</div>
        ) : (
          <form onSubmit={save} className="space-y-8">

            {/* RESEND */}
            <Card icon={Mail} title="Resend — Envío de emails" subtitle="API key y remitente para mandar el cheque y notificaciones.">
              <div className="space-y-4">
                <div>
                  <Label>API Key de Resend</Label>
                  <div className="relative">
                    <input
                      data-testid="settings-resend-key"
                      type={showKey ? "text" : "password"}
                      value={form.resend_api_key}
                      onChange={(e) => set("resend_api_key", e.target.value)}
                      placeholder={meta.resend_api_key_set ? meta.resend_api_key_masked : "re_xxxxxxxxxxxx"}
                      className="w-full pr-10 px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm font-mono"
                    />
                    <button type="button" onClick={() => setShowKey((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#5C5366] hover:text-[#8B1538]">
                      {showKey ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                  <Hint>
                    {meta.resend_api_key_set
                      ? "Ya hay una key guardada. Deja el campo vacío para mantenerla. Pega una nueva para reemplazarla."
                      : "Obtenla en resend.com → API Keys → Create API Key."}
                  </Hint>
                </div>
                <div>
                  <Label>Email remitente (from)</Label>
                  <input
                    data-testid="settings-sender"
                    type="text"
                    value={form.sender_email}
                    onChange={(e) => set("sender_email", e.target.value)}
                    placeholder="Core Colchón <noreply@corecolchon.com>"
                    className="w-full px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm"
                  />
                  <Hint>
                    Mientras no verifiques tu dominio en Resend, usa <span className="font-mono text-[#8B1538]">Core Colchón &lt;onboarding@resend.dev&gt;</span> (solo enviará a tu email registrado en Resend, modo testing).
                  </Hint>
                </div>
              </div>
            </Card>

            {/* TOGGLES */}
            <Card icon={FileText} title="Envíos automáticos" subtitle="Qué emails se disparan cuando entra un nuevo lead.">
              <div className="space-y-3">
                <Toggle
                  testid="toggle-voucher"
                  checked={form.voucher_emails_enabled}
                  onChange={(v) => set("voucher_emails_enabled", v)}
                  title="Enviar cheque PDF al lead"
                  desc="Genera y envía automáticamente el PDF del cheque 100€ al correo del lead."
                />
                <Toggle
                  testid="toggle-notif"
                  checked={form.team_notifications_enabled}
                  onChange={(v) => set("team_notifications_enabled", v)}
                  title="Notificar al equipo comercial"
                  desc="Manda un email al equipo con los datos del lead para llamarle rápido."
                />
                <div>
                  <Label>Email de notificación al equipo</Label>
                  <input
                    data-testid="settings-notify"
                    type="email"
                    value={form.notify_email}
                    onChange={(e) => set("notify_email", e.target.value)}
                    placeholder="ventas@corecolchon.com"
                    className="w-full px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm"
                  />
                  <Hint>Dirección que recibirá el aviso de «Nuevo lead». Puedes poner varios separados por coma más adelante.</Hint>
                </div>
              </div>
            </Card>

            {/* GTM */}
            <Card icon={Tag} title="Google Tag Manager" subtitle="Para medir conversiones desde Meta Ads, Google Ads, GA4…">
              <Label>Container ID de GTM</Label>
              <input
                data-testid="settings-gtm"
                type="text"
                value={form.gtm_id}
                onChange={(e) => set("gtm_id", e.target.value)}
                placeholder="GTM-XXXXXXX"
                className="w-full px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm font-mono"
              />
              <Hint>
                Pega solo el ID (formato <span className="font-mono">GTM-XXXXXXX</span>). El script se inyecta automáticamente en la landing. Déjalo vacío para desactivar.
              </Hint>
            </Card>

            {/* META ADS — CAPI */}
            <Card icon={BarChart3} title="Meta Ads — Píxel y Conversions API" subtitle="Optimiza campañas de Facebook / Instagram con seguimiento server-side (CAPI).">
              <div className="space-y-4">
                <Toggle
                  testid="toggle-meta"
                  checked={form.meta_capi_enabled}
                  onChange={(v) => set("meta_capi_enabled", v)}
                  title="Enviar eventos server-side a Meta"
                  desc="Cuando entre un lead, se envía el evento 'Lead' también desde el servidor (además del Pixel del navegador)."
                />
                <div>
                  <Label>Pixel ID de Meta</Label>
                  <input
                    data-testid="settings-meta-pixel"
                    type="text"
                    value={form.meta_pixel_id}
                    onChange={(e) => set("meta_pixel_id", e.target.value)}
                    placeholder="1364811858864496"
                    className="w-full px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm font-mono"
                  />
                  <Hint>15-16 dígitos. Lo encuentras en Meta Events Manager → tu Pixel → Configuración → ID del pixel.</Hint>
                </div>
                <div>
                  <Label>Access Token de Conversions API</Label>
                  <div className="relative">
                    <input
                      data-testid="settings-meta-token"
                      type={showToken ? "text" : "password"}
                      value={form.meta_capi_token}
                      onChange={(e) => set("meta_capi_token", e.target.value)}
                      placeholder={meta.meta_capi_token_set ? meta.meta_capi_token_masked : "EAAxxxxxxxx…"}
                      className="w-full pr-10 px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm font-mono"
                    />
                    <button type="button" onClick={() => setShowToken((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#5C5366] hover:text-[#8B1538]">
                      {showToken ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                  <Hint>
                    {meta.meta_capi_token_set
                      ? "Ya hay un token guardado. Deja el campo vacío para mantenerlo. Pega uno nuevo para reemplazarlo."
                      : "Genéralo en Events Manager → tu Pixel → Configuración → API de conversiones → Generar token de acceso."}
                  </Hint>
                </div>
                <div>
                  <Label>Test Event Code (opcional)</Label>
                  <input
                    data-testid="settings-meta-test-code"
                    type="text"
                    value={form.meta_test_event_code}
                    onChange={(e) => set("meta_test_event_code", e.target.value)}
                    placeholder="TEST12345"
                    className="w-full px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm font-mono"
                  />
                  <Hint>
                    Si lo rellenas, los eventos aparecen en la pestaña «Test Events» de Events Manager sin contar como conversiones reales. Déjalo vacío para producción.
                  </Hint>
                </div>
                <button
                  data-testid="settings-meta-test-send"
                  type="button"
                  onClick={sendMetaTest}
                  disabled={metaTesting}
                  className="px-4 py-2.5 bg-[#1877F2] hover:bg-[#0e5fc9] disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2"
                >
                  <Send size={14}/> {metaTesting ? "Enviando…" : "Enviar evento de prueba a Meta"}
                </button>
              </div>
            </Card>


            {/* ACTIONS */}
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                data-testid="settings-save"
                type="submit"
                disabled={saving}
                className="px-6 py-3 bg-[#8B1538] hover:bg-[#6A0F2A] disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2"
              >
                <Save size={15}/> {saving ? "Guardando…" : "Guardar cambios"}
              </button>

              <div className="flex-1 min-w-[260px]"/>

              <div className="flex items-stretch gap-2">
                <input
                  data-testid="settings-test-email"
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="email-prueba@tudominio.com"
                  className="px-3 py-2.5 border border-[#E3DDE8] focus:border-[#8B1538] outline-none text-sm w-64"
                />
                <button
                  data-testid="settings-test-send"
                  type="button"
                  onClick={sendTest}
                  disabled={testing}
                  className="px-4 py-2.5 bg-[#2E2638] hover:bg-[#1d1726] disabled:opacity-60 text-white text-sm font-bold uppercase tracking-wider flex items-center gap-2"
                >
                  <Send size={14}/> {testing ? "Enviando…" : "Enviar prueba"}
                </button>
              </div>
            </div>
            <p className="text-xs text-[#5C5366]">
              «Enviar prueba» usa la API key guardada y manda un PDF de cheque al email indicado. Si Resend no acepta el envío, te dirá por qué (key inválida, remitente no verificado…).
            </p>

          </form>
        )}
      </main>
    </div>
  );
}

function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="bg-white border border-[#E3DDE8]">
      <div className="px-6 py-5 border-b border-[#E3DDE8] flex items-start gap-3">
        <div className="w-10 h-10 bg-[#F9F8FA] border border-[#E3DDE8] flex items-center justify-center text-[#8B1538] shrink-0">
          <Icon size={18}/>
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-[#2E2638]">{title}</h3>
          {subtitle && <p className="text-sm text-[#5C5366] mt-0.5">{subtitle}</p>}
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.2em] text-[#5C5366] font-bold mb-1.5">{children}</div>;
}

function Hint({ children }) {
  return <p className="text-xs text-[#5C5366] mt-1.5 leading-relaxed">{children}</p>;
}

function Toggle({ checked, onChange, title, desc, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!checked)}
      className={`w-full flex items-start gap-3 p-3 border text-left transition-colors ${checked ? "bg-[#F9F0F3] border-[#8B1538]" : "bg-white border-[#E3DDE8] hover:border-[#B5AFBE]"}`}
    >
      <span className={`mt-0.5 w-10 h-6 rounded-full flex items-center transition-colors shrink-0 ${checked ? "bg-[#8B1538]" : "bg-[#B5AFBE]"}`}>
        <span className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`}/>
      </span>
      <span className="flex-1">
        <span className="block text-sm font-bold text-[#2E2638]">{title}</span>
        <span className="block text-xs text-[#5C5366] mt-0.5">{desc}</span>
      </span>
    </button>
  );
}
